import type { ReactElement } from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme, type Theme } from '@/lib/theme';

type ThemeOption = {
  value: Theme;
  label: string;
  icon: ReactElement;
};

const OPTIONS: readonly ThemeOption[] = [
  { value: 'light', label: 'Light', icon: <Sun className="h-4 w-4" aria-hidden /> },
  { value: 'warm', label: 'Warm', icon: <Moon className="h-4 w-4" aria-hidden /> },
];

type ThemeToggleProps = {
  className?: string;
};

export function ThemeToggle({ className }: ThemeToggleProps): ReactElement {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border p-1',
        'border-[var(--rule)] bg-[var(--bg-soft)]',
        className,
      )}
    >
      {OPTIONS.map((option) => {
        const isActive = theme === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => setTheme(option.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-all',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1',
              isActive
                ? 'bg-[var(--brand)] text-white shadow-sm'
                : 'text-[var(--ink-3)] hover:text-[var(--ink)]',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
